/** Trips between the Bay Area's hexagons: the H3 res-7 cells that are the model's TAZs
 *  (the same TAZ ids as /od-all and /od-flows), from the income build
 *  (scripts/build_hex_income.py). Two sources: /od-all, every car trip the run routed;
 *  /od-flows, car trips into the charging zone and those the scenario's charge moved off
 *  driving, summed by the hexagon they start in. Travel time from one hexagon to the rest
 *  by each mode comes from Valhalla through /api/link/reach (see there for why transit
 *  comes back as points and the rest as polygons). */

import { cellToBoundary, cellToLatLng, latLngToCell } from 'h3-js';
import { modeTrips } from './flows';

const GRID_URL = '/data/income/hex_income_res7.json';

let gridPromise = null;

/** Every hexagon once per page: { cells: [{ id, taz, population, households,
 *  lowIncomeShare, medianIncome, polygon, lat, lng }], byId, byTaz }. */
export function getHexGrid() {
  if (!gridPromise) {
    gridPromise = fetch(GRID_URL)
      .then((res) => {
        if (!res.ok) throw new Error(`hexagons ${res.status}`);
        return res.json();
      })
      .then((doc) => {
        const f = Object.fromEntries(doc.meta.fields.map((name, i) => [name, i]));
        const cells = Object.entries(doc.hex).map(([id, row]) => {
          const [lat, lng] = cellToLatLng(id);
          return {
            id,
            taz: row[f.taz_id],
            population: row[f.population] || 0,
            households: row[f.households] || 0,
            lowIncomeShare: row[f.low_income_share],
            medianIncome: row[f.median_household_income],
            polygon: cellToBoundary(id).map(([la, ln]) => [ln, la]),
            lat,
            lng,
          };
        });
        return {
          cells,
          byId: new Map(cells.map((c) => [c.id, c])),
          byTaz: new Map(cells.map((c) => [String(c.taz), c])),
        };
      });
    gridPromise.catch(() => { gridPromise = null; });
  }
  return gridPromise;
}

/** Car trips from `originId` in one /od-all document, by destination hexagon.
 *  { origin, trips: [[destHex, count]] heaviest first, without the origin itself, total }. */
export function getHexCarTrips(grid, originId, doc) {
  const o = grid.byId.get(originId);
  if (!o || !doc || doc.origin !== String(o.taz)) return null;
  const trips = [];
  for (const [d, n] of doc.rows) {
    const dest = grid.byTaz.get(d);
    if (dest && dest.id !== originId && n > 0) trips.push([dest.id, n]);
  }
  return { origin: originId, trips, total: doc.total };
}

/** Straight-line distance between two hexagons' centres, in km. */
export function hexDistanceKm(a, b) {
  const rad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * rad;
  const dLng = (b.lng - a.lng) * rad;
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371.0088 * Math.asin(Math.sqrt(h));
}

const zoneTripsCache = new WeakMap();

/** Car trips into the charging zone in one od_flows document (a scenario's run over a
 *  period's hours), by the hexagon they start in: { byHex: hexagon id -> { n, r, c },
 *  n, r, hexes }. n: car trips into the zone; r: those the scenario's charge moved off
 *  driving (0 in the baseline); c: { <class>: [n, r] }, null where the file has none. */
export function getHexZoneTrips(grid, flows) {
  const hit = zoneTripsCache.get(flows);
  if (hit && hit.grid === grid) return hit.out;
  const byHex = new Map();
  let n = 0;
  let r = 0;
  for (const f of flows.flows) {
    const cell = grid.byTaz.get(f.o);
    if (!cell) continue;
    let acc = byHex.get(cell.id);
    if (!acc) {
      acc = { n: 0, r: 0, c: null };
      byHex.set(cell.id, acc);
    }
    const fr = f.r || 0;
    acc.n += f.n;
    acc.r += fr;
    n += f.n;
    r += fr;
    if (f.c) {
      acc.c = acc.c || {};
      for (const k of Object.keys(f.c)) {
        const pair = acc.c[k] || (acc.c[k] = [0, 0]);
        pair[0] += f.c[k][0];
        pair[1] += f.c[k][1];
      }
    }
  }
  let hexes = 0;
  for (const acc of byHex.values()) if (acc.n > 0) hexes += 1;
  const out = { byHex, n, r, hexes };
  zoneTripsCache.set(flows, { grid, out });
  return out;
}

/** Below this many trips into the zone a hexagon's switched share is left uncoloured:
 *  a handful of trips swings it from 0 to 100%. */
export const SHARE_MIN_TRIPS = 20;

/** Hexagon id -> what the flow map's `mode` colours it by (its trips in that mode, or
 *  in 'share' the share that shifted), for all drivers or the income class `classId`;
 *  only for hexagons it colours. */
export function zoneValues(zone, mode, classId = null) {
  const out = new Map();
  for (const [id, t] of zone.byHex) {
    const [n, r] = classId ? (t.c && t.c[classId]) || [0, 0] : [t.n, t.r];
    if (mode === 'share') {
      if (n >= SHARE_MIN_TRIPS) out.set(id, r / n);
    } else {
      const v = modeTrips(mode, n, r);
      if (v > 0) out.set(id, v);
    }
  }
  return out;
}

// ---- travel time -------------------------------------------------------------

export const REACH_MODES = [
  { id: 'walk', label: 'Walk' },
  { id: 'bike', label: 'Bike' },
  { id: 'drive', label: 'Drive' },
  { id: 'transit', label: 'Transit' },
];

/** Band upper edges in minutes, and their colours: brightest is closest. */
export const REACH_BANDS = [
  { upTo: 15, color: [253, 231, 37] },
  { upTo: 30, color: [124, 198, 68] },
  { upTo: 45, color: [31, 161, 135] },
  { upTo: 60, color: [45, 111, 163] },
];

export const reachColor = (minutes) => (REACH_BANDS.find((b) => minutes <= b.upTo) || REACH_BANDS[REACH_BANDS.length - 1]).color;

const reachCache = new Map();

/** Transit leaves at half past this hour when the page names none, as the backend does. */
export const TRANSIT_DEFAULT_HOUR = 8;

/** Transit's answer depends on the hour it leaves; the other modes' do not. */
export const reachKey = (originId, mode, hour) => (mode === 'transit' ? `${originId}:transit@${hour}` : `${originId}:${mode}`);

/** Minutes from `origin` (a grid cell) to every hexagon `mode` reaches within an hour:
 *  Map hexagon id -> minutes. Walk, bike and drive give each hexagon its band's upper
 *  edge (its centre inside the 15-, 30-... minute polygon); transit the earliest arrival
 *  anywhere in the hexagon, leaving at `hour`:30. Rejects with `busy` set when the
 *  backend has too many transit searches running to take another. */
export function getReach(grid, origin, mode, hour = TRANSIT_DEFAULT_HOUR) {
  const key = reachKey(origin.id, mode, hour);
  let p = reachCache.get(key);
  if (!p) {
    p = fetch('/api/link/reach', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ lat: origin.lat, lon: origin.lng, mode, ...(mode === 'transit' ? { hour } : {}) }),
    })
      .then(async (res) => {
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          const e = new Error(body.error || `reach ${res.status}`);
          e.busy = res.status === 503;
          throw e;
        }
        return body;
      })
      .then((doc) => minutesByHex(grid, doc));
    p.catch(() => reachCache.delete(key));
    reachCache.set(key, p);
  }
  return p;
}

function minutesByHex(grid, doc) {
  const out = new Map();
  if (doc.kind === 'points') {
    for (const [lng, lat, minutes] of doc.points) {
      const id = latLngToCell(lat, lng, 7);
      if (grid.byId.has(id) && !(out.get(id) <= minutes)) out.set(id, minutes);
    }
    return out;
  }
  // Smallest band first: a centre takes the first polygon it falls in.
  const contours = [...doc.contours]
    .sort((a, b) => a.minutes - b.minutes)
    .map((c) => ({ minutes: c.minutes, polygons: polygonsOf(c.geometry) }));
  for (const cell of grid.cells) {
    const hit = contours.find((c) => c.polygons.some((poly) => inPolygon(cell.lng, cell.lat, poly)));
    if (hit) out.set(cell.id, hit.minutes);
  }
  return out;
}

/** [{ rings, box }] for a Polygon or MultiPolygon; rings[0] outer, the rest holes. */
function polygonsOf(geometry) {
  const list = geometry.type === 'Polygon' ? [geometry.coordinates]
    : geometry.type === 'MultiPolygon' ? geometry.coordinates : [];
  return list.map((rings) => {
    const xs = rings[0].map((p) => p[0]);
    const ys = rings[0].map((p) => p[1]);
    return { rings, box: [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)] };
  });
}

function inRing(x, y, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

function inPolygon(x, y, { rings, box }) {
  if (x < box[0] || y < box[1] || x > box[2] || y > box[3]) return false;
  return inRing(x, y, rings[0]) && !rings.slice(1).some((hole) => inRing(x, y, hole));
}

/** How many hexagons are within `minutes` in one mode's reach. */
export function reachHexes(minutesTo, minutes) {
  let hexes = 0;
  for (const m of minutesTo.values()) if (m <= minutes) hexes += 1;
  return hexes;
}

// ---- colours -----------------------------------------------------------------

/** Dark to bright, readable on the dark basemap. */
export const TRIP_COLORS = [
  [59, 47, 122],
  [45, 111, 163],
  [31, 161, 135],
  [124, 198, 68],
  [253, 231, 37],
];

/** Up to five bins, evenly spaced on a log scale from 1 to the largest count: trips
 *  run from a handful to thousands, so equal steps would put nearly every hexagon in
 *  the first bin. { colorOf, rows } where rows are the legend's { label, color }. */
export function tripBins(counts) {
  const max = counts.length ? Math.max(...counts) : 0;
  if (max < 1) return { colorOf: () => TRIP_COLORS[0], rows: [] };
  const steps = TRIP_COLORS.length;
  // Upper edge of each bin, rounded, and kept strictly increasing for small maxima.
  const uppers = [];
  for (let i = 1; i <= steps; i++) {
    const v = i === steps ? max : Math.round(max ** (i / steps));
    if (v > (uppers[uppers.length - 1] || 0)) uppers.push(v);
  }
  const colorAt = (i) => TRIP_COLORS[Math.round((i * (steps - 1)) / Math.max(1, uppers.length - 1))];
  const colorOf = (n) => {
    const i = uppers.findIndex((u) => n <= u);
    return colorAt(i === -1 ? uppers.length - 1 : i);
  };
  const rows = uppers.map((hi, i) => {
    const lo = i === 0 ? 1 : uppers[i - 1] + 1;
    return { label: lo === hi ? int(lo) : `${int(lo)}–${int(hi)}`, color: colorAt(i) };
  });
  return { colorOf, rows };
}

/** Up to five equal bins from 0 to the largest share (0–1), in whole percent: shares
 *  spread evenly enough that a log scale would crowd the top bins. Same shape as tripBins. */
export function shareBins(shares) {
  let max = 0;
  for (const v of shares) if (v > max) max = v;
  const steps = TRIP_COLORS.length;
  const step = Math.max(1, Math.ceil((max * 100) / steps));
  const count = Math.max(1, Math.min(steps, Math.ceil((max * 100) / step)));
  const colorAt = (i) => TRIP_COLORS[Math.round((i * (steps - 1)) / Math.max(1, count - 1))];
  const colorOf = (v) => colorAt(Math.min(count - 1, Math.floor((v * 100) / step)));
  const rows = Array.from({ length: count }, (_, i) => ({
    label: `${i * step}–${(i + 1) * step}%`,
    color: colorAt(i),
  }));
  return { colorOf, rows };
}

const int = (n) => Math.round(n).toLocaleString('en-US');
